/* eslint-disable @typescript-eslint/consistent-type-assertions */
import React, { useEffect, useRef } from "react";
import { VideoPlayer, VideoPlayerProps } from "@clarkinc/zest-design-system/ui";
import { cls } from "@wss/cls";
import { Video } from "~/common/Store/models/Video";
import useGtmVideoTracking from "../MediaViewerV2/SharedComponents/VideoViewer/hooks/useGtmVideoTracking";
interface Props {
  posterImage: string;
  sources: Partial<Video["sources"]>;
  activePlayer?: boolean;
  currentIndex: number;
  selected: number;
  autoPlay?: boolean;
  videoClasses?: string;
  youtubeId?: string;
  useCdn?: boolean;
  playsInline?: boolean;
  closedCaptionsrc?: string;
  shouldLoopVideo?: boolean;
  isModal?: boolean;
  videoSection?: string;
}
const ZestVideoPlayer = (props: Props & VideoPlayerProps) => {
  const {
    youtubeId,
    useCdn,
    selected,
    activePlayer,
    currentIndex,
    posterImage,
    videoClasses,
    closedCaptionsrc,
    playsInline,
    sources,
    shouldLoopVideo,
    isModal,
    videoSection,
    autoPlay,
    title,
    ...rest
  } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const baseUrl = useCdn ? "https://cdn.webstaurantstore.com/videos/" : "/videos/";
  const getCaptionUrl = () => {
    if (!closedCaptionsrc) {
      return undefined;
    }
    const captionBaseUrl = "/videos/closed_captioning/";
    return captionBaseUrl + closedCaptionsrc;
  };
  const pauseYoutubeVideo = () => {
    const iframe = document.getElementsByClassName("videoPlayer")[0] as HTMLIFrameElement;
    if (iframe) {
      // resetting the iframe src to pause Youtube video
      const videoSource = iframe.src;
      iframe.src = videoSource;
    }
  };
  useEffect(() => {
    if (currentIndex !== selected) {
      if (youtubeId) {
        pauseYoutubeVideo();
      } else {
        const video = document.querySelector(`.videoPlayer-${currentIndex}`) as HTMLVideoElement;
        if (!video?.paused) {
          video.pause();
        }
      }
    }
  }, [activePlayer, currentIndex, selected, youtubeId]);
  const relativeSources = Object.fromEntries(Object.entries(sources ?? {}).filter(([, value]) => value !== undefined).map(([key, value]) => [key, `${baseUrl}${value}`]));
  const highestSrc = sources?.high || sources?.medium || sources?.low || sources?.product || sources?.ipad || "";
  const {
    sendGtmProgressEvent,
    sendGtmPauseEvent,
    sendGtmPlayEvent
  } = useGtmVideoTracking(videoRef, `${baseUrl}${highestSrc}`, title ?? "Unknown video title", videoSection);

  // This is a workaround as onTimeUpdate is not working with the Zest Player and will be updated at a later time.
  useEffect(() => {
    const currentVideoRef = videoRef.current;
    if (currentVideoRef) {
      currentVideoRef.addEventListener("timeupdate", sendGtmProgressEvent);
    }
    return () => {
      if (currentVideoRef) {
        currentVideoRef.removeEventListener("timeupdate", sendGtmProgressEvent);
      }
    };
  }, [sendGtmProgressEvent]);
  return youtubeId && <iframe title="YouTube Video" src={`//www.youtube.com/embed/${youtubeId}?rel=0&amp${autoPlay ? "&autoplay=1" : ""};hd=1`} className={cls("videoPlayer", "top-0", "left-0", "w-full", "border-0", "aspect-video", isModal && "absolute")} allowFullScreen allow="autoplay" data-testid="youtube-video-player" /> || <VideoPlayer
  // adds prefix to the sources, filters out any sources that are undefined
  sources={relativeSources} className={`videoPlayer-${currentIndex} ${videoClasses}`} data-testid="videoWrapper-container" poster={posterImage} crossOrigin="anonymous" playsInline={playsInline} captionSrc={getCaptionUrl()} loop={shouldLoopVideo} autoPlay={autoPlay} onPlay={sendGtmPlayEvent} onPause={sendGtmPauseEvent} ref={videoRef} {...rest} />;
};
export default ZestVideoPlayer;